import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Film, Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react'
import { getImageUrl } from '../utils/apiUrl'
import apiService from '../services/api'
import toast from 'react-hot-toast'
import AdminLayout from '../components/AdminLayout'

const AdminFilmographie = () => {
  const [filmographie, setFilmographie] = useState([])
  const [editingProject, setEditingProject] = useState(null)
  const [showProjectForm, setShowProjectForm] = useState(false)
  const [isSavingProject, setIsSavingProject] = useState(false)
  
  const navigate = useNavigate()

  // Charger la filmographie au montage du composant
  useEffect(() => {
    fetchFilmographie()
  }, [])

  // Fonctions pour la gestion de la filmographie
  const fetchFilmographie = async () => {
    try {
      const response = await apiService.get('/filmographie')
      setFilmographie(response.data)
    } catch (error) {
      console.error('Erreur chargement filmographie:', error)
      toast.error('Erreur lors du chargement de la filmographie')
    }
  }

  const handleSaveProject = async (projectData) => {
    setIsSavingProject(true)
    try {
      if (editingProject) {
        await apiService.put(`/filmographie/${editingProject.id}`, projectData)
        toast.success('Projet mis à jour avec succès')
      } else {
        await apiService.post('/filmographie', projectData)
        toast.success('Projet créé avec succès')
      }
      await fetchFilmographie()
      setShowProjectForm(false)
      setEditingProject(null)
    } catch (error) {
      console.error('Erreur sauvegarde projet:', error)
      toast.error('Erreur lors de la sauvegarde du projet')
    } finally {
      setIsSavingProject(false)
    }
  }

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
      return
    }
    
    try {
      await apiService.delete(`/filmographie/${projectId}`)
      toast.success('Projet supprimé avec succès')
      await fetchFilmographie()
    } catch (error) {
      console.error('Erreur suppression projet:', error)
      toast.error('Erreur lors de la suppression du projet')
    }
  }

  const handleTogglePublished = async (project) => {
    try {
      await apiService.put(`/filmographie/${project.id}`, {
        ...project,
        is_published: !project.is_published
      })
      toast.success(`Projet ${!project.is_published ? 'publié' : 'dépublié'} avec succès`)
      await fetchFilmographie()
    } catch (error) {
      console.error('Erreur toggle publication:', error)
      toast.error('Erreur lors de la mise à jour du projet')
    }
  }

  return (
    <AdminLayout title="Filmographie" subtitle="Gestion des projets cinématographiques">
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Gestion de la filmographie</h3>
            <p className="text-sm text-gray-600">Gérez les projets cinématographiques affichés sur le site</p>
          </div>
          <button
            onClick={() => {
              setEditingProject(null)
              setShowProjectForm(true)
            }}
            className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau projet
          </button>
        </div>

        {/* Liste des projets */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="space-y-4">
              {filmographie.map((project) => (
                <div key={project.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Miniature de l'image */}
                    <div className="flex-shrink-0">
                      {project.photo_jacquette ? (
                        <img
                          src={getImageUrl(project.photo_jacquette)}
                          alt={`Affiche de ${project.nom_projet}`}
                          className="w-16 h-20 object-cover rounded-lg shadow-sm border border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'flex'
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-16 h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center"
                        style={{ display: project.photo_jacquette ? 'none' : 'flex' }}
                      >
                        <Film className="h-6 w-6 text-gray-400" />
                      </div>
                    </div>
                    
                    {/* Informations du projet */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="font-medium text-gray-900">{project.nom_projet}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          project.is_published 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {project.is_published ? 'Publié' : 'Brouillon'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {project.type_projet} • {project.annee_sortie || 'Année non spécifiée'}
                      </p>
                      {project.realisateur && (
                        <p className="text-xs text-gray-500">Réalisateur: {project.realisateur}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleTogglePublished(project)}
                      className={`p-2 rounded transition-colors ${
                        project.is_published 
                          ? 'text-green-600 hover:text-green-700' 
                          : 'text-gray-600 hover:text-gray-700'
                      }`}
                      title={project.is_published ? 'Dépublier' : 'Publier'}
                    >
                      {project.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => {
                        setEditingProject(project)
                        setShowProjectForm(true)
                      }}
                      className="p-2 text-gray-600 hover:text-orange-600 transition-colors"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProject(project.id)}
                      className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              
              {filmographie.length === 0 && (
                <div className="text-center py-8">
                  <Film className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Aucun projet</h4>
                  <p className="text-gray-600 mb-4">Commencez par ajouter votre premier projet cinématographique.</p>
                  <button
                    onClick={() => {
                      setEditingProject(null)
                      setShowProjectForm(true)
                    }}
                    className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un projet
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Formulaire de projet */}
        {showProjectForm && (
          <ProjectForm
            project={editingProject}
            onSave={handleSaveProject}
            onCancel={() => {
              setShowProjectForm(false)
              setEditingProject(null)
            }}
            isSaving={isSavingProject}
          />
        )}
      </div>
    </AdminLayout>
  )
}

// Composant formulaire de projet (copié exactement d'AdminSettings)
const ProjectForm = ({ project, onSave, onCancel, isSaving }) => {
  const [formData, setFormData] = useState({
    nom_projet: '',
    type_projet: 'Film',
    annee_sortie: '',
    photo_jacquette: '',
    realisateur: '',
    lieu_tournage: '',
    synopsis: '',
    is_published: false,
    sort_order: 0
  })

  useEffect(() => {
    if (project) {
      setFormData({
        nom_projet: project.nom_projet || '',
        type_projet: project.type_projet || 'Film',
        annee_sortie: project.annee_sortie || '',
        photo_jacquette: project.photo_jacquette || '',
        realisateur: project.realisateur || '',
        lieu_tournage: project.lieu_tournage || '',
        synopsis: project.synopsis || '',
        is_published: project.is_published || false,
        sort_order: project.sort_order || 0
      })
    }
  }, [project])

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Si un fichier est sélectionné, l'uploader d'abord
    if (selectedFile) {
      try {
        // Convertir l'image en base64
        const reader = new FileReader()
        reader.onloadend = async () => {
          try {
            const response = await apiService.post('/upload/image', {
              image: reader.result,
              filename: selectedFile.name,
              folder: 'filmographie'
            })

            if (response.data && response.data.path) {
              // Mettre à jour le formData avec le chemin de l'image
              const updatedFormData = {
                ...formData,
                photo_jacquette: response.data.path
              }
              onSave(updatedFormData)
            }
          } catch (error) {
            console.error('Erreur upload image:', error)
            toast.error('Erreur lors de l\'upload de l\'image')
          }
        }
        reader.readAsDataURL(selectedFile)
      } catch (error) {
        console.error('Erreur lecture fichier:', error)
        toast.error('Erreur lors de la lecture du fichier')
      }
    } else {
      // Pas de fichier sélectionné, sauvegarder directement
      onSave(formData)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const [selectedFile, setSelectedFile] = useState(null)
  const [availableImages, setAvailableImages] = useState([])

  // Charger les images disponibles
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await apiService.get('/upload/images?folder=filmographie')
        setAvailableImages(response.data.images || [])
      } catch (error) {
        console.error('Erreur chargement images:', error)
      }
    }
    fetchImages()
  }, [])

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validation de la taille (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 5MB)')
      return
    }

    // Validation du type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner un fichier image')
      return
    }

    setSelectedFile(file)
    toast.info('Image sélectionnée : ' + file.name)
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-4">
        {project ? 'Modifier le projet' : 'Nouveau projet'}
      </h4>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nom du projet */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom du projet *
            </label>
            <input
              type="text"
              name="nom_projet"
              value={formData.nom_projet}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: Le Secret des Caraïbes"
            />
          </div>

          {/* Type de projet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de projet *
            </label>
            <select
              name="type_projet"
              value={formData.type_projet}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="Film">Film</option>
              <option value="Publicité">Publicité</option>
              <option value="Clip Vidéo">Clip Vidéo</option>
              <option value="Court-Métrage">Court-Métrage</option>
              <option value="Documentaire">Documentaire</option>
              <option value="Série TV">Série TV</option>
              <option value="Téléfilm">Téléfilm</option>
            </select>
          </div>

          {/* Année de sortie */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Année de sortie
            </label>
            <input
              type="number"
              name="annee_sortie"
              value={formData.annee_sortie}
              onChange={handleChange}
              min="1900"
              max="2100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="2024"
            />
          </div>

          {/* Réalisateur */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Réalisateur
            </label>
            <input
              type="text"
              name="realisateur"
              value={formData.realisateur}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: Jean-Pierre Martin"
            />
          </div>

          {/* Lieu de tournage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lieu de tournage
            </label>
            <input
              type="text"
              name="lieu_tournage"
              value={formData.lieu_tournage}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: Guadeloupe"
            />
          </div>

          {/* Photo jacquette */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Photo (jacquette du projet)
            </label>
            
            {/* Upload d'image */}
            <div className="mb-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                {selectedFile ? `✅ Fichier sélectionné : ${selectedFile.name}` : 'Formats acceptés : JPG, PNG, WebP (max 5MB)'}
              </p>
              {selectedFile && (
                <p className="text-xs text-orange-600 mt-1">
                  💡 L'image sera uploadée lors de la sauvegarde du projet
                </p>
              )}
            </div>

            <div className="mb-3 text-center text-sm text-gray-500">
              ou
            </div>

            {/* Sélecteur d'image */}
            <div className="mb-3">
              <select
                name="photo_jacquette"
                value={formData.photo_jacquette}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Sélectionner une image existante</option>
                {availableImages.map((img) => (
                  <option key={img} value={img}>
                    {img.split('/').pop()}
                  </option>
                ))}
              </select>
            </div>

            {/* Aperçu de l'image */}
            {formData.photo_jacquette && (
              <div className="mb-3 text-center">
                <div className="inline-block bg-gray-50 p-4 rounded-xl">
                  <img
                    src={getImageUrl(formData.photo_jacquette)}
                    alt="Aperçu jacquette"
                    className="max-w-[200px] max-h-[280px] w-auto h-auto object-contain rounded-lg shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {formData.photo_jacquette.split('/').pop()}
                </p>
              </div>
            )}
          </div>

          {/* Synopsis */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Synopsis
            </label>
            <textarea
              name="synopsis"
              value={formData.synopsis}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Description du projet..."
            />
          </div>

          {/* Ordre d'affichage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ordre d'affichage
            </label>
            <input
              type="number"
              name="sort_order"
              value={formData.sort_order}
              onChange={handleChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Plus le nombre est petit, plus le projet apparaîtra en premier
            </p>
          </div>

          {/* Publication */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_published"
              checked={formData.is_published}
              onChange={handleChange}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Publier sur le site
            </label>
          </div>
        </div>

        {/* Boutons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Sauvegarde...' : (project ? 'Mettre à jour' : 'Créer')}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdminFilmographie